import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  TrendingUp, 
  TrendingDown,
  FileText,
  Building,
  Wallet,
  PieChart,
  BarChart3,
  RefreshCw,
  Calendar,
  ChevronDown,
  AlertCircle,
  Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function BilanDashboardPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [bilanData, setBilanData] = useState(null);
  const [periodType, setPeriodType] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [showMissingDetails, setShowMissingDetails] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadBilanData();
  }, [token, periodType, selectedMonth, selectedYear]);

  const loadBilanData = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/accounting/bilan/dashboard?`;
      if (periodType === 'month') {
        url += `month=${selectedMonth}`;
      } else {
        url += `year=${selectedYear}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBilanData(response.data);
    } catch (error) {
      console.error('Erreur chargement bilan:', error);
      toast.error('Erreur lors du chargement du bilan');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'from-green-500/20 to-green-600/10 border-green-500/30';
    if (score >= 50) return 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
    return 'from-red-500/20 to-red-600/10 border-red-500/30';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Priorité haute';
      case 'medium': return 'Priorité moyenne';
      case 'low': return 'Priorité basse';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Chargement du bilan...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/accounting')} 
              variant="ghost" 
              size="sm"
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour Comptabilité
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <PieChart className="w-6 h-6 text-indigo-400" />
                Tableau de Bord Bilan
              </h1>
              <p className="text-white/60 text-sm">Suivi et détection des manquements</p>
            </div>
          </div>
          
          {/* Sélecteur de période */}
          <div className="flex items-center gap-3">
            <div className="flex bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setPeriodType('month')}
                className={`px-3 py-1 rounded text-sm transition-all ${
                  periodType === 'month' 
                    ? 'bg-indigo-500 text-white' 
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Mois
              </button>
              <button
                onClick={() => setPeriodType('year')}
                className={`px-3 py-1 rounded text-sm transition-all ${
                  periodType === 'year' 
                    ? 'bg-indigo-500 text-white' 
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Année
              </button>
            </div>
            
            {periodType === 'month' ? (
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm"
              />
            ) : (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm"
              >
                {[-2, -1, 0, 1].map(offset => {
                  const year = new Date().getFullYear() + offset;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            )}
            
            <Button 
              onClick={loadBilanData}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {bilanData && (
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Score de complétude */}
          <div className={`bg-gradient-to-br ${getScoreBg(bilanData.completeness_score)} border rounded-2xl p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white/80 text-sm font-medium mb-1">Score de Complétude</h2>
                <div className={`text-5xl font-bold ${getScoreColor(bilanData.completeness_score)}`}>
                  {bilanData.completeness_score}%
                </div>
                <p className="text-white/60 text-sm mt-2">
                  {bilanData.completeness_score >= 80 
                    ? 'Excellent ! Votre bilan est bien renseigné.'
                    : bilanData.completeness_score >= 50
                    ? 'Attention : Certaines charges récurrentes sont manquantes.'
                    : 'Alerte : De nombreux éléments importants sont manquants.'}
                </p>
              </div>
              <div className="text-right">
                {bilanData.completeness_score >= 80 ? (
                  <CheckCircle2 className="w-16 h-16 text-green-500" />
                ) : bilanData.completeness_score >= 50 ? (
                  <AlertTriangle className="w-16 h-16 text-yellow-500" />
                ) : (
                  <XCircle className="w-16 h-16 text-red-500" />
                )}
              </div>
            </div>
          </div>

          {/* Statistiques principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
                <FileText className="w-4 h-4" />
                Documents
              </div>
              <div className="text-2xl font-bold text-white">{bilanData.summary.total_documents}</div>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-red-400 text-sm mb-2">
                <TrendingDown className="w-4 h-4" />
                Dépenses
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(bilanData.summary.total_expenses)}</div>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
                <TrendingUp className="w-4 h-4" />
                Avoirs
              </div>
              <div className="text-2xl font-bold text-green-400">{formatCurrency(Math.abs(bilanData.summary.total_avoirs))}</div>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 text-orange-400 text-sm mb-2">
                <Wallet className="w-4 h-4" />
                Net
              </div>
              <div className="text-2xl font-bold text-white">{formatCurrency(bilanData.summary.net_expenses)}</div>
            </div>
          </div>

          {/* Éléments manquants */}
          {bilanData.missing_items.length > 0 && (
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-6">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowMissingDetails(!showMissingDetails)}
              >
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Éléments Manquants ({bilanData.missing_items.length})
                </h3>
                <ChevronDown className={`w-5 h-5 text-white/60 transition-transform ${showMissingDetails ? 'rotate-180' : ''}`} />
              </div>
              
              {showMissingDetails && (
                <div className="mt-4 space-y-3">
                  {bilanData.missing_items.map((item, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border ${getPriorityColor(item.priority)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-white">{item.category}</div>
                          <div className="text-sm text-white/70 mt-1">{item.message}</div>
                          <div className="text-xs text-white/50 mt-2 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            {item.suggestion}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(item.priority)}`}>
                          {getPriorityLabel(item.priority)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Avertissements */}
          {bilanData.warnings.length > 0 && (
            <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                Avertissements ({bilanData.warnings.length})
              </h3>
              <div className="space-y-2">
                {bilanData.warnings.map((warning, index) => (
                  <div 
                    key={index}
                    className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20"
                  >
                    <div className="text-sm text-yellow-200">{warning.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Fournisseurs */}
          {bilanData.top_suppliers.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-indigo-400" />
                Top 5 Fournisseurs
              </h3>
              <div className="space-y-3">
                {bilanData.top_suppliers.map((supplier, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-300 font-medium">
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-white font-medium">{supplier.name}</div>
                        <div className="text-white/50 text-xs">{supplier.count} document(s)</div>
                      </div>
                    </div>
                    <div className={`font-mono font-medium ${supplier.total < 0 ? 'text-green-400' : 'text-white'}`}>
                      {formatCurrency(supplier.total)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Catégories attendues */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              Catégories Attendues pour un Bilan Complet
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {bilanData.expected_categories.map((cat, index) => {
                const isMissing = bilanData.missing_items.some(m => m.category === cat.name);
                const catData = Object.entries(bilanData.by_category).find(
                  ([id, data]) => id.toLowerCase().includes(cat.id) || cat.name.toLowerCase().includes(id.toLowerCase())
                );
                
                return (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg border ${
                      isMissing 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : catData 
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white/80">{cat.name}</span>
                      {isMissing ? (
                        <XCircle className="w-4 h-4 text-red-400" />
                      ) : catData ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border border-white/30" />
                      )}
                    </div>
                    {catData && (
                      <div className="text-xs text-white/50">
                        {catData[1].count} doc(s) - {formatCurrency(catData[1].total)}
                      </div>
                    )}
                    <div className={`text-xs mt-1 ${
                      cat.priority === 'high' ? 'text-red-400' : 
                      cat.priority === 'medium' ? 'text-yellow-400' : 'text-blue-400'
                    }`}>
                      {cat.type === 'fixe' ? 'Charge fixe' : 'Charge variable'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 pt-4">
            <Button
              onClick={() => navigate('/accounting')}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              <FileText className="w-4 h-4 mr-2" />
              Ajouter des documents
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
